import React, { useEffect } from 'react';
import dayjs from 'dayjs';

import combinedActions from '~redux/combinedActions';
import { useAppDispatch, useAppSelector } from '~redux/hooks';
import { initialState as approvalInitialState } from '~redux/slices/approval';

import SearchAndFilterBar, { TSearchAndFilterBarProps } from '~components/molecules/SearchAndFilterBar';

import * as Styles from './_collateralApprovalTableSearchAndFilterBar.module.scss';
import { TPendingCollateralApprovalListingFilters } from '~types/feat/customer';
import { MetalTypeEnum, TransactionStatusEnum } from '~utils/enums';

const CollateralApprovalTableSearchAndFilterBar = () => {
    const dispatch = useAppDispatch();

    const filters = useAppSelector(state => state.approval.pendingCollateralListing.filters);
    const branchesList = useAppSelector(state => state.branch.allBranches);
    const { allBranchAccess, branchCode } = useAppSelector(state => state.auth.userDetails);

    const initialFilters = approvalInitialState.pendingCollateralListing.filters;

    const setFilters = (
        selectedFilters: TPendingCollateralApprovalListingFilters
    ) => dispatch(
        combinedActions.approvalSetPendingCollateralListingFilters(selectedFilters)
    );

    const handleSearchClick = () => {
        dispatch(combinedActions.approvalGetPendingCollateralListingAttempt());
    };

    const handleCancelClick = () => {
        setFilters({
            ...initialFilters,
            branchCode: !allBranchAccess ? branchCode : '',
            branchName: !allBranchAccess
                ? (branchesList.find(
                    branch => branch.branchCode === branchCode
                )?.branchName || '')
                : '',
        });

        handleSearchClick();
    };

    useEffect(() => {
        if (branchesList.length > 0 && !allBranchAccess) {
            setFilters({
                ...filters,
                branchCode,
                branchName: branchesList.find(
                    branch => branch.branchCode === branchCode
                )?.branchName || '',
            });
        }
    }, [branchesList]);

    const massagedBranchesListOption = () => {
        if (!branchesList.length) return [];

        return branchesList.map(branch => ({
            label: branch.branchName,
            value: branch.branchCode,
        }));
    };

    const fields: TSearchAndFilterBarProps['fields'] = [
        {
            label: 'Customer Name:',
            type: 'input' as const,
            inputParams: {
                value: filters.customerName || '',
                setValue: event => setFilters({
                    ...filters,
                    customerName: event.target.value || '',
                }),
                onKeyDown: event => {
                    if (event.key === 'Enter') {
                        handleSearchClick();
                    }
                },
                className: Styles.input,
            },
        },
        {
            label: 'ID No.:',
            type: 'input' as const,
            inputParams: {
                value: filters.customerId
                    ? filters.customerId.toString()
                    : '',
                setValue: event => setFilters({
                    ...filters,
                    customerId: Number(event.target.value),
                }),
                onKeyDown: event => {
                    if (event.key === 'Enter') {
                        handleSearchClick();
                    }
                },
                className: Styles.input,
            },
        },
        {
            label: 'Branch:',
            type: 'dropdown' as const,
            dropdownParams: {
                options: massagedBranchesListOption(),
                value: filters.branchName,
                setValue: (event: React.ChangeEvent<HTMLInputElement>) => setFilters({
                    ...filters,
                    branchName: event.target.value,
                    branchCode: branchesList.find(
                        branch => branch.branchName === event.target.value
                    )?.branchCode || '',
                }),
                optionSelected: !!branchesList.find(
                    branch => branch.branchName === filters.branchName
                ),
                noOptionsMessage: 'No branch found',
                disabled: !allBranchAccess,
            },
        },
        {
            label: 'Date From:',
            type: 'datepicker' as const,
            datepickerParams: {
                date: filters.dateFrom,
                setDate: (date: string) => {
                    if (date > filters?.dateTo) {
                        setFilters({
                            ...filters,
                            dateFrom: date,
                            dateTo: '',
                        });
                    } else {
                        setFilters({
                            ...filters,
                            dateFrom: date,
                        });
                    }
                },
                startDate: filters.dateFrom,
                maxDate: dayjs().toISOString(),
                endDate: filters.dateTo,
                selectsStart: true,
            },
        },
        {
            label: 'Date To:',
            type: 'datepicker' as const,
            datepickerParams: {
                date: filters.dateTo,
                minDate: filters.dateFrom,
                setDate: (date: string) => setFilters({
                    ...filters,
                    dateTo: date,
                }),
                startDate: filters.dateFrom,
                endDate: filters.dateTo,
                selectsEnd: true,
            },
        },
        {
            label: 'Metal Type:',
            type: 'dropdown' as const,
            dropdownParams: {
                options: [
                    {
                        label: 'Gold',
                        value: MetalTypeEnum.Gold,
                    },
                    {
                        label: 'Silver',
                        value: MetalTypeEnum.Silver,
                    },
                ],
                value: filters.metalType,
                setValue: (event: React.ChangeEvent<HTMLInputElement>) => setFilters({ ...filters, metalType: event.target.value }),
                optionSelected: !!MetalTypeEnum[filters.metalType as keyof typeof MetalTypeEnum],
                noOptionsMessage: 'No metal type found',
            },
        },
    ];

    return (
        <SearchAndFilterBar
            fields={fields}
            searchOnClick={handleSearchClick}
            cancelOnClick={handleCancelClick}
            containerClassName={Styles['content-container']}
        />
    );
};

export default CollateralApprovalTableSearchAndFilterBar;
